// src/db/models/assessmentSettingsModel.js
//
// Assessment-settings data-access layer.
// All network calls now go through the shared API client, so the admin
// bearer token is automatically attached and the 403 issue is resolved.

import { api } from '../../api/client';
import { assessmentIdOf, sameAssessment, settingIsAssigned } from '../../utils/assessmentIdentity';

export const AssessmentSettingsModel = {
  /* ------------------------------------------------------------ *
   * CRUD HELPERS
   * ------------------------------------------------------------ */
  async create(settingData) {
    try {
      if (
        !settingData.user_id ||
        !assessmentIdOf(settingData) ||
        !settingData.access_code
      ) {
        throw new Error(
          'Missing required fields: user_id, assessment_type, and access_code are required'
        );
      }

      const assessmentId = assessmentIdOf(settingData);
      return api.createAssessmentSetting({
        ...settingData,
        assessmentId,
        assessment_id: assessmentId,
        assessment_type: assessmentId,
      });
    } catch (err) {
      if (err.message?.includes('UNIQUE constraint failed')) return null;
      console.error('Error creating assessment setting:', err);
      throw err;
    }
  },

  async createBulk(users, assessmentType, isVisible = true) {
    try {
      const targets = await api.getAssessmentAssignmentTargets(assessmentType);
      const revisionByUser = new Map(
        (targets.users || []).map(user => [Number(user.id), Number(user.settingRevision || 0)])
      );
      return api.bulkAssignAssessment(
        assessmentType,
        users.map(userId => ({
          userId,
          assigned: isVisible,
          expectedRevision: revisionByUser.get(Number(userId)) ?? 0
        }))
      );
    } catch (err) {
      console.error('Error in bulk creation:', err);
      throw err;
    }
  },

  async findByAccessCode(accessCode) {
    if (!accessCode) throw new Error('Access code is required');
    return api.getAssessmentSettings({ accessCode });
  },

  async findAll() {
    return api.getAllAssessmentSettings();
  },

  async findByAssessmentType(assessmentType) {
    const all = await this.findAll();
    return all.filter((s) => sameAssessment(s, assessmentType));
  },

  async update(id, data) {
    if (!id) throw new Error('Setting ID is required');
    return api.updateAssessmentSetting(id, data);
  },

  async delete(id, expectedRevision) {
    if (!id) throw new Error('Setting ID is required');
    return api.deleteAssessmentSetting(id, expectedRevision);
  },

  async deleteByAssessmentType(assessmentType) {
    const list = await this.findByAssessmentType(assessmentType);
    return Promise.all(list.map((s) => this.delete(s.id, s.row_revision)));
  },

  async toggleVisibility(id, isVisible) {
    return this.update(id, { is_visible: isVisible });
  },

  async toggleVisibilityBulk(assessmentType, isVisible) {
    const list = await this.findByAssessmentType(assessmentType);
    return Promise.all(
      list.map((s) => this.toggleVisibility(s.id, isVisible))
    );
  },

  /* ------------------------------------------------------------ *
   * MAINTENANCE HELPERS
   * ------------------------------------------------------------ */

  /**
   * Delete assessment-setting rows whose user record no longer exists.
   * Uses authenticated API helpers – no more 403.
   */
  async cleanupOrphanedSettings() {
    try {
      // 1) Get current users & settings (authFetch => bearer token)
      const [users, settings] = await Promise.all([
        api.getAllUsers(),
        this.findAll(),
      ]);

      const validIds = new Set(users.map((u) => u.id));
      const orphans = settings.filter((s) => !validIds.has(s.user_id));

      if (!orphans.length)
        return {
          success: true,
          message: 'No orphaned settings found',
          cleanedCount: 0,
        };

      // 2) Delete each orphaned row
      const results = await Promise.all(
        orphans.map((s) =>
          this
            .delete(s.id)
            .then(() => ({
              success: true,
              settingId: s.id,
              userId: s.user_id,
            }))
            .catch((err) => ({
              success: false,
              settingId: s.id,
              userId: s.user_id,
              error: err.message,
            }))
        )
      );

      const ok = results.filter((r) => r.success).length;

      return {
        success: true,
        message: `Successfully cleaned up ${ok} of ${orphans.length} orphaned settings`,
        cleanedCount: ok,
        totalOrphanedCount: orphans.length,
        details: results,
      };
    } catch (err) {
      console.error('Error cleaning up orphaned settings:', err);
      return {
        success: false,
        message: `Cleanup failed: ${err.message}`,
        error: err,
      };
    }
  },

  async isAssignedToUser(assessmentType, userId) {
    if (!assessmentType || !userId)
      throw new Error('Assessment type and user ID are required');

    const all = await this.findAll();
    return all.some(
      (s) =>
        Number(s.user_id) === Number(userId) &&
        sameAssessment(s, assessmentType) &&
        settingIsAssigned(s)
    );
  },
};
